import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

/** Private, user-created tags. Normalized server-side so duplicates can't accumulate. */
export default function ContactTags({ contactId, tags = [], onChanged }) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");

  const add = async () => {
    const value = label.trim();
    if (!value) return;
    try {
      await api.post(`/crm/contacts/${contactId}/tags`, { label: value });
      setLabel(""); setAdding(false);
      onChanged?.();
    } catch { toast.error("Couldn't add that tag"); }
  };

  const remove = async (tagId) => {
    try {
      await api.delete(`/crm/contacts/${contactId}/tags/${tagId}`);
      onChanged?.();
    } catch { toast.error("Couldn't remove that tag"); }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="contact-tags">
      {tags.map((t) => (
        <span key={t.id} data-testid={`contact-tag-${t.norm}`}
          className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-[560] text-foreground/80">
          {t.label}
          <button type="button" onClick={() => remove(t.id)} aria-label={`Remove tag ${t.label}`}
            data-testid={`contact-tag-remove-${t.norm}`}
            className="rounded-full p-0.5 text-muted-foreground hover:text-destructive">
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {adding ? (
        <span className="inline-flex items-center gap-1">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} autoFocus
            aria-label="New tag" data-testid="contact-tag-input" className="h-8 w-36 text-sm"
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
              if (e.key === "Escape") { setAdding(false); setLabel(""); }
            }} />
          <button type="button" onClick={add} data-testid="contact-tag-confirm"
            className="rounded-full bg-primary px-2.5 py-1 text-xs font-[600] text-primary-foreground">
            Add
          </button>
        </span>
      ) : (
        <button type="button" onClick={() => setAdding(true)} data-testid="contact-tag-add"
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-1 text-xs font-[560] text-muted-foreground transition-colors hover:border-primary hover:text-primary">
          <Plus className="h-3 w-3" /> Tag
        </button>
      )}
    </div>
  );
}
